/**
 * What the GitHub App round trip came back with, in words. The callback
 * (`routes/auth/github.ts`) lands the browser on the page the flow started
 * from with one outcome param; every page that starts the flow (Connections,
 * Create, Work, Doors) reads it here, so the words are the same everywhere.
 * `githubPendingInstall` is not here: a GitHub-initiated install always lands
 * on Connections, which confirms it there.
 */

export type GitHubInstallOutcome =
  | { kind: "error"; message: string }
  | { kind: "requested"; message: string }
  | { kind: "cancelled"; message: string };

export const GITHUB_OUTCOME_PARAMS = ["githubError", "githubRequested", "githubCancelled"] as const;

const ERROR_MESSAGES: Record<string, string> = {
  verification_failed:
    "GitHub connected, but YAGNI could not verify repository access. Check the app settings and reconnect.",
  admin_required:
    "Only a workspace admin can finish connecting GitHub. Ask an admin to sign in and use Link existing.",
  installation_not_accessible:
    "GitHub did not list that installation under your account. Sign in to GitHub as a member of the organization where YAGNI was installed, then use Link existing.",
  installation_not_found:
    "GitHub did not recognize that installation as YAGNI's. Use Link existing to pick the right one.",
  no_installation:
    "GitHub did not list a YAGNI installation under your account. Install the app first, or sign in to GitHub as a member of the organization where it is installed.",
  authorization_failed: "GitHub did not finish signing you in. Try Connect GitHub again.",
  invalid_state: "That GitHub page was opened from an old or different tab. Start again from Connect GitHub.",
  missing_code: "GitHub came back without saying what happened. Try Connect GitHub again.",
  token_exchange_failed: "GitHub did not finish signing you in. Try again in a minute.",
  installations_unavailable: "GitHub did not list your installations just now. Try again in a minute.",
};

export function githubErrorMessage(code: string): string {
  return Object.hasOwn(ERROR_MESSAGES, code)
    ? ERROR_MESSAGES[code]!
    : "GitHub connection failed. Please try reconnecting.";
}

export const GITHUB_REQUESTED_MESSAGE =
  "Sent to the owners of your GitHub organization. Once they approve, press Check again.";

export const GITHUB_CANCELLED_MESSAGE =
  "GitHub connection was cancelled before it finished. Click Connect GitHub to try again.";

export function readGitHubInstallOutcome(search: string): GitHubInstallOutcome | null {
  const params = new URLSearchParams(search);
  const error = params.get("githubError");
  if (error) return { kind: "error", message: githubErrorMessage(error) };
  if (params.has("githubRequested")) return { kind: "requested", message: GITHUB_REQUESTED_MESSAGE };
  if (params.has("githubCancelled")) return { kind: "cancelled", message: GITHUB_CANCELLED_MESSAGE };
  return null;
}
